import { Component, HostBinding, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { FieldExpression, LuceneExpression, term } from 'lucene';
import { debounceTime, distinctUntilChanged, firstValueFrom, Subscription } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgIf } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { TranslationService } from '../../../shared/services/translation.service';
import { OrderItem } from '../../../order/models/order-item';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { WorkflowStepContent } from '../../../shared/components/workflow-steps/workflow-steps.component';
import { UserDatacenterView } from '../../../shared/models/datacenter-aggrid-view';
import { Selection } from '../../../shared/models/selection';
import { OrderBy, OrderByDirection } from '../../../shared/utils/constant';
import { CallableOrderItem, EcallOffByVolumeStepState } from '../../models/ecall-off-model';
import { ECallOffByVolumeViewService } from '../../services/ecall-off-by-volume-view-service';
import { ECallOffService } from '../../services/ecall-off.service';
import { DialogEcallOffComponent } from '../dialog-ecall-off/dialog-ecall-off.component';
import { MillSelectorMill, MillSelectorComponent } from '../mill/mill-selector.component';
import {
	normalizeViewName,
	resolveViewFromParam
} from '../../../shared/utils/datacenter-view-utils';

import { ByVolumeOrderListComponent } from './by-volume-order-item-list.component';

@Component({
	selector: 'copo-by-volume-step-material-picking',
	templateUrl: './by-volume-step-material-picking.component.html',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	styleUrl: './by-volume-step-material-picking.component.css',
	imports: [
		MillSelectorComponent,
		TranslateModule,

		MatFormFieldModule,

		MatInputModule,
		FormsModule,
		ReactiveFormsModule,
		NgIf,

		MatButtonModule,
		MatIconModule,
		ByVolumeOrderListComponent
	]
})
export class ByVolumeStepMaterialPickingComponent implements OnInit, OnDestroy, WorkflowStepContent {
	@HostBinding('class.passthrough') readonly passthrough = true;

	mills?: MillSelectorMill[];
	orderItems: CallableOrderItem[] = [];
	orderItemsViews: UserDatacenterView[] = [];
	orderItemsCurrentView: UserDatacenterView;
	quickSearchFilter?: LuceneExpression;
	viewFilter?: LuceneExpression;
	searchTextControl = new FormControl('', { nonNullable: true });
	searchTextSubscription: Subscription;
	selectedOrderItems: CallableOrderItem[] = [];
	stepState?: EcallOffByVolumeStepState;

	constructor(

		private readonly dialog: MatDialog,
		private readonly ecallOffService: ECallOffService,
		private readonly ecallOffViewService: ECallOffByVolumeViewService,
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly translationService: TranslationService,
		private readonly snackBar: MatSnackBar
	) {
		// watch search input
		this.searchTextSubscription = this.watchSearchTextControl();
		const defaultView = this.ecallOffViewService.getDefaultView();
		this.orderItemsViews = this.ecallOffViewService.getViewList(defaultView);
		const queryParams = this.route.snapshot.queryParamMap;
		const viewParam = queryParams.get('view');
		this.orderItemsCurrentView = viewParam
			? this.resolveViewFromUrlParam(viewParam, defaultView)
			: this.ecallOffViewService.getCurrentView(defaultView);
		this.viewFilter = this.ecallOffViewService.convertViewToLuceneFilter(this.orderItemsCurrentView);
	}

	ngOnInit(): void {
		if (!this.mills || this.mills.length === 0) {
			this.getMills()
				.then(mills => {
					this.setMills(mills, false);
				})
				.catch((error: unknown) => {
					console.error('Error during init', error);
				});
		}
	}

	ngOnDestroy(): void {
		this.searchTextSubscription.unsubscribe();
	}

	parseOrderItems(orderItems: OrderItem[]): CallableOrderItem[] {
		return orderItems.map(orderItem => {
			const selectedOrderItem = this.selectedOrderItems.find(soi => soi.id === orderItem.id);
			return selectedOrderItem ? {
				...orderItem,
				calledWeight: selectedOrderItem.calledWeight,
				comment: selectedOrderItem.comment,
				requestedDeliveryDateFrom: selectedOrderItem.requestedDeliveryDateFrom,
				requestedDeliveryDateTo: selectedOrderItem.requestedDeliveryDateTo,
				unloadingReference: selectedOrderItem.unloadingReference,
				shipTo: selectedOrderItem.shipTo
			} : orderItem;
		});
	}

	canGoBack() {
		return this.selectedOrderItems.length === 0;
	}

	getOrderItems() {
		const selectedMillCodes = this.mills?.filter(m => m.selected).map(m => m.code);
		if (!selectedMillCodes || selectedMillCodes.length === 0) {
			this.orderItems = [];
			return;
		}
		const page = 1;
		const pageSize = 10_000;
		const finalFilter = this.createFinalSearchFilter(this.quickSearchFilter, this.viewFilter);
		this.ecallOffService.getCallableOrderItems(
			[
				'cdd',
				'customerOrderNumber',
				'customerOrderItemNumber',
				'length',
				'mill',
				'millDescription',
				'qtyBatchGross',
				'qtyBatchNet',
				'steelgradeDescription',
				'supplierOrderNumber',
				'supplierOrderItemNumber',
				'thickness',
				'width',
				'shipTo',
				'shipToDescription',
				'serviceLevel',
				'serviceLevelDescription',
				'soldTo',
				'soldToDescription'
			],
			finalFilter,
			page,
			pageSize,
			new OrderBy('customerOrderNumber', OrderByDirection.ASC),
			selectedMillCodes
		).then(result => {
			this.orderItems = this.parseOrderItems(result.results);
		}).catch((error: unknown) => {
			console.error('Error when getting order items', error);
		});
	}

	createFinalSearchFilter(currentFilter?: LuceneExpression, viewFilter?: LuceneExpression) {
		if (currentFilter && viewFilter) {
			return {
				left: currentFilter,
				operator: 'AND',
				right: viewFilter,
				parenthesized: true
			};
		}
		return currentFilter ?? viewFilter;
	}

	hasSelectedOrderItems() {
		return this.selectedOrderItems.length > 0;
	}

	async onChangeView(view: UserDatacenterView) {
		this.orderItemsCurrentView = view;
		this.viewFilter = this.ecallOffViewService.convertViewToLuceneFilter(view);
		this.getOrderItems();
		await this.ecallOffViewService.saveCurrentView(view);
		await this.router.navigate([], {
			relativeTo: this.route,
			queryParams: { view: normalizeViewName(view.name) },
			replaceUrl: true
		});
	}

	async onCustomizeView(view: UserDatacenterView) {
		this.ecallOffViewService.setViewToEdit(view);
		const state = this.getUpdatedStepState();
		await this.router.navigate(['view'], { relativeTo: this.route, state });
	}

	onSelectOrder(selection: Selection<CallableOrderItem>) {
		this.selectedOrderItems = this.selectedOrderItems.filter(oi => oi.id !== selection.data.id);
		if (selection.selected) {
			selection.data.calledWeight = selection.data.qtyBatchGross;
			this.selectedOrderItems.push(selection.data);
		}
	}

	onDisplayOrder(orderItem: CallableOrderItem) {
		if (!orderItem.supplierOrderNumber || !orderItem.supplierOrderItemNumber) {
			console.warn('Missing supplier order reference or item to display', {
				reference: orderItem.supplierOrderNumber,
				item: orderItem.supplierOrderItemNumber
			});
			return;
		}
		const url =
			`/order-tracking/order/card/${ orderItem.supplierOrderNumber }/${ orderItem.supplierOrderItemNumber }`;
		open(url, '_blank');
	}

	async getMills(): Promise<MillSelectorMill[]> {
		return firstValueFrom(this.ecallOffService.getEcallMills()).then(batches => batches.map(batch => ({
			code: batch.mill,
			description: batch.millDescription,
			selected: true
		}))).catch((error: unknown) => {
			console.error('Error when getting mills', error);
			return [];
		});
	}

	createValidateMillsChange(): (mills: MillSelectorMill[]) => Promise<boolean> {
		// when passing callback to another component
		// 'this' reference the child component instead of the actual
		// so use a factory to keep reference of the actual component
		// eslint-disable-next-line @typescript-eslint/no-this-alias
		const self = this;
		return async (mills: MillSelectorMill[]) => {
			// if no edited order items, you can change
			// if selected order items mills are still in the selected mills, you can change
			// else ask user to confirm change because current editing will be lost
			const selectedOrderItemsFromUnselectedMills =
				self.filterOrderItemsFromMills(self.selectedOrderItems, mills, false);
			if (selectedOrderItemsFromUnselectedMills.length === 0) {
				return true; // cause nothing to reset, so can change
			}
			return self.openMillChangeDialog();
		};
	}

	async openMillChangeDialog(): Promise<boolean> {
		// if nothing selected return true
		// else open a dialog to ask confirmation
		if (!this.hasSelectedOrderItems()) {
			return true;
		}
		const dialogRef = this.dialog.open(DialogEcallOffComponent, {
			data: {}
		});
		return <boolean> await firstValueFrom(dialogRef.afterClosed());
	}

	onMillsChange(mills?: MillSelectorMill[]) {
		this.setMills(mills ?? [], true);
	}

	setMills(mills: MillSelectorMill[], force: boolean) {
		if (!force && this.mills && this.mills.length > 0) {
			return;
		}
		this.mills = mills;
		this.orderItems = [];
		this.selectedOrderItems = this.filterOrderItemsFromMills(this.selectedOrderItems, mills, true);
		this.getOrderItems();
	}

	filterOrderItemsFromMills(orderItems: CallableOrderItem[], mills: MillSelectorMill[], selected: boolean) {
		const millCodes = new Set(mills.filter(mill => mill.selected === selected).map(mill => mill.code));
		return orderItems.filter(orderItem => millCodes.has(orderItem.mill ?? ''));
	}

	updateQuickSearchFilter(searchString: string) {
		if (!searchString.trim()) {
			this.quickSearchFilter = undefined;
			return;
		}
		const escapedTextFilter = `*${ term.escape(searchString) }*`;

		const customerOrderNumberField: FieldExpression = {
			field: 'customerOrderNumber',
			term: escapedTextFilter,
			quoted: false
		};

		const supplierOrderNumberField: FieldExpression = {
			field: 'supplierOrderNumber',
			term: escapedTextFilter,
			quoted: false
		};

		const steelgradeDescriptionField: FieldExpression = {
			field: 'steelgrade',
			term: escapedTextFilter,
			quoted: false
		};

		this.quickSearchFilter = {
			left: {
				left: customerOrderNumberField,
				operator: 'OR',
				right: supplierOrderNumberField,
				parenthesized: true
			},
			operator: 'OR',
			right: steelgradeDescriptionField,
			parenthesized: true
		};
	}

	watchSearchTextControl() {
		return this.searchTextControl.valueChanges.pipe(
			debounceTime(500),
			distinctUntilChanged()
		).subscribe(searchString => {
			this.updateQuickSearchFilter(searchString);
			this.getOrderItems();
		});
	}

	isStepValidated() {
		return this.hasSelectedOrderItems();
	}

	setStepState(state: Record<string, unknown> | undefined) {
		if (state) {
			this.stepState = <EcallOffByVolumeStepState> <unknown> state;
			this.setMills(this.stepState.mills ?? [], true);
			this.selectedOrderItems = this.stepState.selectedOrderItems ?? [];
		}
	}

	getUpdatedStepState() {
		const newState: EcallOffByVolumeStepState = {
			...this.stepState,
			mills: this.mills,
			selectedOrderItems: this.selectedOrderItems
		};
		return newState;
	}

	private resolveViewFromUrlParam(viewParam: string | null, defaultView: UserDatacenterView): UserDatacenterView {
		const res = resolveViewFromParam(viewParam, this.orderItemsViews, defaultView);
		if (!res.found) {
			this.snackBar.open(
				this.translationService.getTR('datacenter.view-not-found', { viewName: res.decodedViewName }),
				undefined,
				{ duration: 3000 }
			);
		}
		return res.view;
	}
}
